import React, { useContext } from "react";
import "./PrivacyPolicy.css";
import { useNavigate } from "react-router-dom";
import { logout } from "../utils/firebase";
import GlobalNavDrawer from "../components/GlobalNavDrawer";
import { TokenContext } from "./tokenContext"; // import TokenContext

function PrivacyPolicy() {
  const lastUpdated = "September 26, 2025";
  const { balance } = useContext(TokenContext);
  const navigate = useNavigate();
    const navigateTo = (path) => {
      navigate(path);
    };
  return (
    <main className="TOS" role="main" id="top">
      {/* Background glows */}
      <div className="tosGlow tosGlow--a" aria-hidden="true" />
      <div className="tosGlow tosGlow--b" aria-hidden="true" />

      {/* Page header */}
      <header className="tosHeader">
        <h1 className="tosTitle">Privacy Policy</h1>
        <p className="tosStamp">The iSpeakWell Project · Last updated {lastUpdated}</p>
      </header>

      {/* Layout: sticky ToC + content */}
      <div className="tosLayout">
        <aside className="tosToc" aria-label="Table of contents">
          <nav>
            <ol>
              <li><a href="#scope">Scope & Agreement</a></li>
              <li><a href="#info-we-collect">Information We Collect</a></li>
              <li><a href="#how-we-use">How We Use Information</a></li>
              <li><a href="#legal-bases">Legal Bases (GDPR)</a></li>
              <li><a href="#sharing">Sharing & Disclosure</a></li>
              <li><a href="#podcast-privacy">Live Participation & Public Exposure</a></li>
              <li><a href="#transfers">International Transfers</a></li>
              <li><a href="#retention">Data Retention</a></li>
              <li><a href="#your-rights">Your Rights</a></li>
              <li><a href="#children">Children’s Privacy</a></li>
              <li><a href="#security">Security</a></li>
              <li><a href="#cookies">Cookies & Tracking / DNT</a></li>
              <li><a href="#links">Links to Other Sites</a></li>
              <li><a href="#breach">Data Breach</a></li>
              <li><a href="#changes">Changes to this Policy</a></li>
              <li><a href="#contact">Contact Us</a></li>
            </ol>
          </nav>
        </aside>

        <article className="tosContent">
          {/* Scope */}
          <section id="scope" className="tosCard">
            <h2>Scope &amp; Agreement</h2>
            <p>
              This Privacy Policy describes how Language Play Studio (“Company”, “we”, “us”) collects, uses, and shares information in connection with The iSpeakWell Project website and related services (“Service”). By using the Service, you agree to this Policy.
            </p>
            <p>
              If you use the Service on behalf of an organization, you represent that you have authority to bind that organization to this Policy.
            </p>
          </section>

          {/* Information We Collect */}
          <section id="info-we-collect" className="tosCard">
            <h2>Information We Collect</h2>
            <h3>Information you provide</h3>
            <ul>
              <li>Account data (e.g., name, email).</li>
              <li>Profile and usage inputs within learning tools and features.</li>
              <li>Support requests and communications.</li>
            </ul>
            <h3>Automatically collected</h3>
            <ul>
              <li>Technical data (browser, device, general location), usage logs, diagnostics.</li>
              <li>Cookies or similar technologies for session continuity and security (see Cookies &amp; Tracking).</li>
            </ul>
            <h3>Sensitive and children’s data</h3>
            <p>We do not knowingly collect data from children under 18. Do not provide data for a child without appropriate authorization (see Children’s Privacy).</p>
          </section>

          {/* How We Use */}
          <section id="how-we-use" className="tosCard">
            <h2>How We Use Information</h2>
            <ul>
              <li>Provide, maintain, and improve the Service and core learning features.</li>
              <li>Authenticate users, secure accounts, prevent abuse and fraud.</li>
              <li>Process purchases and token accounting; send transactional notices.</li>
              <li>Respond to requests; provide support and troubleshooting.</li>
              <li>Comply with legal obligations and enforce terms.</li>
            </ul>
          </section>

          {/* Legal Bases */}
          <section id="legal-bases" className="tosCard">
            <h2>Legal Bases (GDPR)</h2>
            <p>Where applicable, we process personal data under one or more of the following bases:</p>
            <ul>
              <li><strong>Consent</strong> (e.g., optional communications).</li>
              <li><strong>Contract</strong> (to deliver the Service you requested).</li>
              <li><strong>Legal obligation</strong> (e.g., tax, accounting, compliance).</li>
              <li><strong>Legitimate interests</strong> (e.g., Service security, quality, and analytics proportionate to user impact).</li>
            </ul>
          </section>

          {/* Sharing */}
          <section id="sharing" className="tosCard">
            <h2>Sharing &amp; Disclosure</h2>
            <p>
              We do not sell your personal information. We may share limited data with service providers who help us operate the Service (under contracts that require appropriate safeguards), and disclose information where required by law or to protect rights, safety, and integrity.
            </p>
          </section>
          <section id="podcast-privacy" className="tosCard">
            <h2>Live Participation & Public Exposure</h2>
            <p>
              If you join the “Podcast” room, we process your audio/video and related speech-analysis signals to deliver coaching and feedback.
              We may publish or rebroadcast podcast recordings in whole or in part on our channels (including YouTube) and third-party platforms.
              This is based on your consent. You can withdraw consent for future recordings at any time by contacting
              <a className="PRV-LINK" href="mailto:privacy@ispeakwell.ca"> privacy@ispeakwell.ca</a>.
            </p>
            <p>
              Public appearance carries risks, including potential recognition or doxxing by unaffiliated individuals. While we take reasonable steps to moderate our channels,
              we cannot control third parties. By participating, you accept this risk.
            </p>
            <p>
              We retain recordings as reasonably necessary for educational, moderation, safety, and archival purposes consistent with this Policy.
              Previously published content may persist in accordance with platform policies and applicable law.
            </p>
          </section>
          {/* Transfers */}
          <section id="transfers" className="tosCard">
            <h2>International Transfers</h2>
            <p>
              If information is transferred across borders, we use appropriate safeguards permitted by applicable law (e.g., contractual clauses).
            </p>
          </section>

          {/* Retention */}
          <section id="retention" className="tosCard">
            <h2>Data Retention</h2>
            <p>
              We keep personal data only as long as necessary for the purposes described above, to comply with legal obligations, resolve disputes, and enforce agreements. We may retain de-identified or aggregated data that does not identify you.
            </p>
          </section>

          {/* Your Rights */}
          <section id="your-rights" className="tosCard">
            <h2>Your Rights</h2>
            <p>
              Depending on your location, you may have rights to access, correct, delete, or port your data; object to or restrict processing; or withdraw consent. To exercise rights, contact us at <a className="PRV-LINK" href="mailto:privacy@ispeakwell.ca">privacy@ispeakwell.ca</a>. We will respond within timeframes required by law.
            </p>
          </section>

          {/* Children */}
          <section id="children" className="tosCard">
            <h2>Children’s Privacy</h2>
            <p>
              We do not knowingly collect personal information from children under 18. If you believe a child provided data, contact us and we will delete it.
            </p>
          </section>

          {/* Security */}
          <section id="security" className="tosCard">
            <h2>Security</h2>
            <p>
              We use reasonable technical and organizational measures to protect personal information. No method is 100% secure; transmission over the Internet involves risks you acknowledge.
            </p>
          </section>

          {/* Cookies / DNT */}
          <section id="cookies" className="tosCard">
            <h2>Cookies &amp; Tracking / Do Not Track</h2>
            <p>
              We use necessary cookies for authentication and session continuity. Some browsers send “Do Not Track” signals; there is no consensus on how to respond. We currently do not respond to DNT signals, but we limit tracking to what is necessary to operate the Service.
            </p>
          </section>

          {/* Links */}
          <section id="links" className="tosCard">
            <h2>Links to Other Sites</h2>
            <p>
              Third-party sites are not controlled by us; review their privacy policies before providing information.
            </p>
          </section>

          {/* Breach */}
          <section id="breach" className="tosCard">
            <h2>Data Breach</h2>
            <p>
              If we become aware of a breach affecting your personal information, we will take appropriate steps, including notifying you and/or authorities when required by law.
            </p>
          </section>

          {/* Changes */}
          <section id="changes" className="tosCard">
            <h2>Changes to this Policy</h2>
            <p>
              We may update this Policy from time to time. Material changes will be communicated via reasonable notice. Your continued use of the Service after updates indicates acceptance.
            </p>
          </section>

          {/* Contact */}
          <section id="contact" className="tosCard tosCard--muted">
            <h2>Contact Us</h2>
            <p>
              Questions about this Policy? Email <a className="PRV-LINK" href="mailto:privacy@ispeakwell.ca">privacy@ispeakwell.ca</a> or <a className="PRV-LINK" href="mailto:info@ispeakwell.ca">info@ispeakwell.ca</a>.
            </p>
            <p className="tosNote">This summary is for convenience and does not replace applicable privacy laws.</p>
          </section>

          <footer className="tosFooter">
            <a
              className="tosTop"
              href="#top"
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            >
              ▲ Back to top
            </a>
          </footer>
        </article>
      </div>

      <GlobalNavDrawer
        hideCurrent
        links={[
          { to: "/purchase", label: `Tokens: ${balance}`},
          { to: "/voicebot", label: "iSpeakWell" },
          { to: "/resumerevisor", label: "Resumes" },
          { to: "/conversationpractice", label: "Convos" },
          { to: "/interview-practice", label: "Interviews" },
          { to: "/termsofservice", label: "Terms" },
          { to: "/admin", label: "Admin" },
          { to: "/login", label: "Logout", onClick: async () => { await logout(); } },
        ]}
      />

      <div className="TOS-links-section">
        <button className="TOS-link-button" onClick={() => navigateTo("/about")}>About</button>
        <button className="TOS-link-button" onClick={() => navigateTo("/careers")}>Careers</button>
        <button className="TOS-link-button" onClick={() => navigateTo("/tokens")}>Tokens</button>
        <button className="TOS-link-button" onClick={() => navigateTo("/contactus")}>Contact Us</button>
        <button className="TOS-link-button" onClick={() => navigateTo("/login")}>Login</button>
      </div>
    </main>
  );
}

export default PrivacyPolicy;
